import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import {
  getForwardTrace,
  getBatchSuggestions,
  ForwardTraceResponse,
} from '@/services/traceabilityService';

export default function ForwardTracePage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [traceData, setTraceData] = useState<ForwardTraceResponse['data']>(null);

  // Fetch suggestions when search term changes
  useEffect(() => {
    async function loadSuggestions() {
      try {
        const res = await getBatchSuggestions('forward', searchTerm);
        if (res && res.success) {
          setSuggestions(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load batch suggestions:', err);
      }
    }
    loadSuggestions();
  }, [searchTerm]);

  const handleSearch = async (batch?: string) => {
    const target = (batch || searchTerm).trim();
    if (!target) return;

    setSelectedBatch(target);
    setShowSuggestions(false);
    setLoading(true);

    try {
      const res = await getForwardTrace(target);
      if (res && res.success && res.found && res.data) {
        setTraceData(res.data);
      } else {
        setTraceData(null);
      }
    } catch (err) {
      console.error('Failed to get forward trace:', err);
      setTraceData(null);
    } finally {
      setLoading(false);
    }
  };

  const productionUsages = traceData?.productionUsages || traceData?.links || [];
  const directSales = traceData?.directSales || [];
  const birth = traceData?.birth;

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/traceability')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Batch Traceability"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Forward Batch Trace</h1>
              <p className="text-sm text-[#64748b] mt-0.5">
                Trace a raw material or GRN batch from birth through production, invoices, and customers
              </p>
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
            <label className="block text-sm font-bold text-[#1e293b] mb-2">
              Enter Raw Material or Inward Batch Number
            </label>
            <div className="flex items-stretch gap-3">
              <div className="flex-1 relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-barcode-line text-base" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="e.g. B-GRN-2026-0001, RM-STEEL-01..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
                {showSuggestions && searchTerm && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">
                    {suggestions.map((b) => (
                      <div
                        key={b}
                        onMouseDown={() => {
                          setSearchTerm(b);
                          handleSearch(b);
                        }}
                        className="px-4 py-2.5 text-sm text-[#1e293b] hover:bg-sky-50 cursor-pointer flex items-center gap-2 font-medium"
                      >
                        <i className="ri-barcode-box-line text-sky-600" />
                        {b}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleSearch()}
                disabled={loading}
                className="px-6 py-2.5 rounded-xl bg-[#2563eb] text-white text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
                Trace Journey
              </button>
            </div>
          </div>

          {/* Results State */}
          {selectedBatch && !loading && !traceData && (
            <div className="bg-white border border-amber-200 rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-amber-50 border border-amber-200 mb-4">
                <i className="ri-error-warning-line text-2xl text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-[#1e293b] mb-1">No Trace Found</h3>
              <p className="text-sm text-[#64748b]">
                Batch &quot;{selectedBatch}&quot; has no recorded inward or movement history in the system.
              </p>
            </div>
          )}

          {traceData && (
            <div className="space-y-6">
              {/* Batch Master Info Card (Birth + Warehouse Status) */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-5 border-b border-slate-100">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-blue-50 text-blue-600 border border-blue-100 shrink-0">
                      <i className="ri-barcode-box-line text-2xl" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h2 className="text-xl font-bold text-[#1e293b]">{traceData.batchNumber}</h2>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                            traceData.status === 'EXHAUSTED'
                              ? 'bg-slate-100 text-slate-600 border-slate-200'
                              : traceData.status === 'PARTIAL'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }`}
                        >
                          {traceData.status || 'ACTIVE'}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">
                          {traceData.batchType || 'RAW_MATERIAL'}
                        </span>
                      </div>
                      <p className="text-sm text-[#64748b] mt-1 font-medium">
                        {traceData.itemName || traceData.inputItemName} &bull; Warehouse: {traceData.warehouseName}
                      </p>
                    </div>
                  </div>

                  {/* Stock Metrics */}
                  <div className="grid grid-cols-3 gap-6 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold uppercase">Initial Inward</div>
                      <div className="text-lg font-bold text-slate-800 mt-0.5">
                        {traceData.initialQty ?? traceData.totalQtyUsed} {traceData.inputUnit}
                      </div>
                    </div>
                    <div className="text-center border-x border-slate-200 px-4">
                      <div className="text-xs text-slate-500 font-semibold uppercase">Consumed / Sold</div>
                      <div className="text-lg font-bold text-amber-700 mt-0.5">
                        {(traceData.totalProductionUsed || 0) + (traceData.totalDirectSold || 0)}{' '}
                        {traceData.inputUnit}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold uppercase">In Warehouse</div>
                      <div className="text-lg font-bold text-emerald-700 mt-0.5">
                        {traceData.currentQty ?? 0} {traceData.inputUnit}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Birth & Supplier Details Grid */}
                {birth && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 text-xs">
                    <div>
                      <span className="text-slate-400 font-medium uppercase">Source Document</span>
                      <p className="font-semibold text-slate-800 mt-0.5 flex items-center gap-1.5">
                        <i className="ri-file-text-line text-blue-500" />
                        {birth.grnNumber}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium uppercase">Supplier / Vendor</span>
                      <p className="font-semibold text-slate-800 mt-0.5 flex items-center gap-1.5">
                        <i className="ri-building-line text-blue-500" />
                        {birth.supplierName}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium uppercase">Supplier Batch #</span>
                      <p className="font-semibold text-slate-800 mt-0.5 flex items-center gap-1.5">
                        <i className="ri-hashtag text-blue-500" />
                        {birth.supplierBatchNo || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-400 font-medium uppercase">Received / Inward Date</span>
                      <p className="font-semibold text-slate-800 mt-0.5 flex items-center gap-1.5">
                        <i className="ri-calendar-line text-blue-500" />
                        {birth.receivedDate || '-'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Visual Journey Tree */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-bold text-[#1e293b] flex items-center gap-2">
                    <i className="ri-git-branch-line text-blue-600" />
                    Complete Journey & Distribution Tree
                  </h3>
                  <span className="text-xs text-slate-500 font-medium">
                    Birth &rarr; Production Usage &rarr; Invoices & Customers &rarr; Remaining Balance
                  </span>
                </div>

                {/* Root Node */}
                <div className="relative pl-6 pb-6 border-l-2 border-blue-400">
                  <div className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-blue-600 border-4 border-white shadow-sm" />
                  <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700">
                          STAGE 1: BIRTH (GRN INWARD)
                        </span>
                        <h4 className="text-base font-bold text-slate-900 mt-0.5">
                          {traceData.batchNumber} &bull; {traceData.itemName} ({traceData.initialQty} {traceData.inputUnit})
                        </h4>
                        <p className="text-xs text-slate-600 mt-0.5">
                          Received from <span className="font-semibold">{birth?.supplierName}</span> via{' '}
                          <span className="font-semibold">{birth?.grnNumber}</span> on {birth?.receivedDate}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 font-semibold text-xs rounded-lg">
                        Inward Batch Origin
                      </span>
                    </div>
                  </div>
                </div>

                {/* Branch 1: Consumed in Production */}
                {productionUsages.length > 0 && (
                  <div className="relative pl-6 pb-6 border-l-2 border-indigo-400">
                    <div className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-indigo-600 border-4 border-white shadow-sm" />
                    <div className="mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">
                        STAGE 2: USED IN PRODUCTION ({productionUsages.length} Order
                        {productionUsages.length > 1 ? 's' : ''})
                      </span>
                    </div>

                    <div className="space-y-4 mt-2">
                      {productionUsages.map((usage) => (
                        <div
                          key={usage.id}
                          className="bg-white border border-indigo-100 rounded-xl p-4 shadow-xs ml-2"
                        >
                          <div className="flex items-start justify-between flex-wrap gap-2 pb-3 border-b border-indigo-50">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-indigo-900">
                                  {usage.productionOrderNumber}
                                </span>
                                <span className="text-xs text-slate-500 font-medium">
                                  &bull; {usage.productionDate} &bull; Operator: {usage.operatorName}
                                </span>
                              </div>
                              <p className="text-xs text-slate-600 mt-0.5">
                                Consumed: <span className="font-semibold text-amber-700">{usage.inputQtyUsed ?? '—'} {usage.inputUnit}</span> &rarr; Produced:{' '}
                                <span className="font-semibold text-emerald-700">
                                  {usage.outputQtyProduced} {usage.outputUnit}
                                </span>
                              </p>
                            </div>
                            <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-lg text-xs font-semibold">
                              FG Batch: {usage.outputBatchNumber} ({usage.outputItemName})
                            </div>
                          </div>

                          {/* Downstream Invoices of this FG Batch */}
                          <div className="mt-3 pl-4 border-l-2 border-emerald-200">
                            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                              Sold to Customers via Sales Invoices:
                            </span>
                            {usage.downstreamInvoices && usage.downstreamInvoices.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1.5">
                                {usage.downstreamInvoices.map((inv, idx) => (
                                  <div
                                    key={idx}
                                    className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2.5 text-xs flex items-center justify-between"
                                  >
                                    <div>
                                      <p className="font-bold text-slate-800">{inv.invoiceNumber}</p>
                                      <p className="text-slate-600 font-medium">{inv.customerName}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-emerald-800">
                                        {inv.qtySold} {inv.unit}
                                      </p>
                                      <p className="text-[11px] text-slate-500">{inv.invoiceDate}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500 italic mt-1">
                                FG Batch produced &bull; Currently stored in warehouse stock (Not yet invoiced).
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Branch 2: Direct Sales */}
                {directSales.length > 0 && (
                  <div className="relative pl-6 pb-6 border-l-2 border-amber-400">
                    <div className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-amber-600 border-4 border-white shadow-sm" />
                    <div className="mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                        DIRECT SALES ({directSales.length} Invoice{directSales.length > 1 ? 's' : ''})
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 ml-2">
                      {directSales.map((sale, idx) => (
                        <div
                          key={idx}
                          className="bg-amber-50/50 border border-amber-200 rounded-xl p-3.5 text-xs flex items-center justify-between"
                        >
                          <div>
                            <span className="font-bold text-slate-900 text-sm">{sale.invoiceNumber}</span>
                            <p className="text-slate-700 font-medium mt-0.5">{sale.customerName}</p>
                            <p className="text-slate-500 text-[11px]">{sale.invoiceDate}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-amber-800">
                              {sale.qtySold} {sale.unit}
                            </span>
                            <p className="text-slate-600 font-medium">₹{sale.amount?.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Branch 3: Current Warehouse Balance */}
                <div className="relative pl-6">
                  <div className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-emerald-600 border-4 border-white shadow-sm" />
                  <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                          STAGE 3: CURRENT WAREHOUSE STOCK
                        </span>
                        <h4 className="text-base font-bold text-slate-900 mt-0.5">
                          {traceData.currentQty ?? 0} {traceData.inputUnit} Available
                        </h4>
                        <p className="text-xs text-slate-600 mt-0.5">
                          Location: <span className="font-semibold">{traceData.warehouseName}</span> &bull; Status:{' '}
                          <span className="font-semibold">{traceData.status}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            navigate('/manufacturing/traceability/recall', {
                              state: {
                                batchNumber: selectedBatch,
                                batchType: 'raw',
                                affectedProducts: productionUsages,
                              },
                            })
                          }
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold transition-colors cursor-pointer shadow-sm"
                        >
                          <i className="ri-alarm-warning-line text-sm" />
                          Simulate Recall
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}